import { baseApi } from "../baseApi";

const peerSupportApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Member
    getOnCallWorkers: builder.query({
      query: () => "/api/peer-support/available-workers",
      providesTags: [{ type: "PeerSupportWorker", id: "ON_CALL" }],
    }),
    getPeerSupportCategories: builder.query({
      query: () => "/api/peer-support/categories",
      providesTags: [{ type: "PeerSupportCategory", id: "LIST" }],
    }),
    submitPeerSupportRequest: builder.mutation({
      query: (body) => ({
        url: "/api/peer-support/requests",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "PeerSupportRequest", id: "MY_LIST" }],
    }),
    getMyPeerSupportRequests: builder.query({
      query: () => "/api/peer-support/my-requests",
      providesTags: [{ type: "PeerSupportRequest", id: "MY_LIST" }],
    }),
    getPeerSupportRequest: builder.query({
      query: (id) => `/api/peer-support/requests/${id}`,
      providesTags: (r, e, id) => [{ type: "PeerSupportRequest", id }],
    }),
    getMyPeerSupportTeam: builder.query({
      query: () => "/api/peer-support/my-team",
      providesTags: [{ type: "PeerSupportWorker", id: "MY_TEAM" }],
    }),
    updateMyPeerSupportTeamPreference: builder.mutation({
      query: ({ workerId, isApproved }) => ({
        url: `/api/peer-support/my-team/${workerId}`,
        method: "PATCH",
        body: { isApproved },
      }),
      invalidatesTags: [{ type: "PeerSupportWorker", id: "MY_TEAM" }],
    }),

    // Worker
    getWorkerPendingRequests: builder.query({
      query: () => "/api/peer-support/worker/pending",
      providesTags: [{ type: "PeerSupportRequest", id: "WORKER_PENDING" }],
    }),
    getWorkerActiveRequests: builder.query({
      query: () => "/api/peer-support/worker/active",
      providesTags: [{ type: "PeerSupportRequest", id: "WORKER_ACTIVE" }],
    }),
    getWorkerCompletedRequests: builder.query({
      query: () => "/api/peer-support/worker/completed",
      providesTags: [{ type: "PeerSupportRequest", id: "WORKER_COMPLETED" }],
    }),
    acceptPeerSupportRequest: builder.mutation({
      query: (id) => ({
        url: `/api/peer-support/requests/${id}/accept`,
        method: "PATCH",
      }),
      invalidatesTags: (r, e, id) => [
        { type: "PeerSupportRequest", id },
        { type: "PeerSupportRequest", id: "WORKER_PENDING" },
        { type: "PeerSupportRequest", id: "WORKER_ACTIVE" },
        { type: "PeerSupportRequest", id: "ADMIN_LIST" },
      ],
    }),
    closePeerSupportRequest: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/peer-support/requests/${id}/close`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (r, e, { id }) => [
        { type: "PeerSupportRequest", id },
        { type: "PeerSupportRequest", id: "WORKER_ACTIVE" },
        { type: "PeerSupportRequest", id: "WORKER_COMPLETED" },
        { type: "PeerSupportRequest", id: "ADMIN_LIST" },
        { type: "PeerSupportStats", id: "LIST" },
      ],
    }),
    togglePeerSupportOnCall: builder.mutation({
      query: (body) => ({
        url: "/api/peer-support/worker/on-call",
        method: "PATCH",
        body,
      }),
      invalidatesTags: [
        { type: "PeerSupportWorker", id: "ON_CALL" },
        { type: "PeerSupportWorker", id: "MY_STATUS" },
      ],
    }),
    getMyPeerSupportWorkerStatus: builder.query({
      query: () => "/api/peer-support/worker/my-status",
      providesTags: [{ type: "PeerSupportWorker", id: "MY_STATUS" }],
    }),

    // Admin
    adminGetAllPeerSupportRequests: builder.query({
      query: (params) => ({ url: "/api/peer-support/admin/requests", params }),
      providesTags: [{ type: "PeerSupportRequest", id: "ADMIN_LIST" }],
    }),
    adminGetPeerSupportStats: builder.query({
      query: (params) => ({ url: "/api/peer-support/admin/stats", params }),
      providesTags: [{ type: "PeerSupportStats", id: "LIST" }],
    }),
    getPeerSupportSettings: builder.query({
      query: () => "/api/peer-support/admin/settings",
      providesTags: [{ type: "PeerSupportSettings", id: "SINGLETON" }],
    }),
    updatePeerSupportSettings: builder.mutation({
      query: (body) => ({
        url: "/api/peer-support/admin/settings",
        method: "PUT",
        body,
      }),
      invalidatesTags: [{ type: "PeerSupportSettings", id: "SINGLETON" }],
    }),
    getPeerSupportWorkerRoster: builder.query({
      query: () => "/api/peer-support/admin/workers",
      providesTags: [{ type: "PeerSupportWorker", id: "ROSTER" }],
    }),
    adminAddPeerSupportWorker: builder.mutation({
      query: (body) => ({
        url: "/api/peer-support/admin/workers",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "PeerSupportWorker", id: "ROSTER" }],
    }),
    adminRemovePeerSupportWorker: builder.mutation({
      query: (workerId) => ({
        url: `/api/peer-support/admin/workers/${workerId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "PeerSupportWorker", id: "ROSTER" }],
    }),
    adminGetPeerSupportCategories: builder.query({
      query: () => "/api/peer-support/admin/categories",
      providesTags: [{ type: "PeerSupportCategory", id: "ADMIN_LIST" }],
    }),
    adminCreatePeerSupportCategory: builder.mutation({
      query: (body) => ({
        url: "/api/peer-support/admin/categories",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "PeerSupportCategory", id: "ADMIN_LIST" },
        { type: "PeerSupportCategory", id: "LIST" },
      ],
    }),
    adminUpdatePeerSupportCategory: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/peer-support/admin/categories/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [{ type: "PeerSupportCategory", id: "ADMIN_LIST" }],
    }),
    adminDeletePeerSupportCategory: builder.mutation({
      query: (id) => ({
        url: `/api/peer-support/admin/categories/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "PeerSupportCategory", id: "ADMIN_LIST" }],
    }),
    adminGetPeerSupportDispositions: builder.query({
      query: () => "/api/peer-support/admin/dispositions",
      providesTags: [{ type: "PeerSupportDisposition", id: "ADMIN_LIST" }],
    }),
    adminCreatePeerSupportDisposition: builder.mutation({
      query: (body) => ({
        url: "/api/peer-support/admin/dispositions",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "PeerSupportDisposition", id: "ADMIN_LIST" }],
    }),
    adminUpdatePeerSupportDisposition: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/peer-support/admin/dispositions/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [{ type: "PeerSupportDisposition", id: "ADMIN_LIST" }],
    }),
    adminDeletePeerSupportDisposition: builder.mutation({
      query: (id) => ({
        url: `/api/peer-support/admin/dispositions/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "PeerSupportDisposition", id: "ADMIN_LIST" }],
    }),
    adminGetWorkerApprovalCounts: builder.query({
      query: () => "/api/peer-support/admin/worker-approval-counts",
      providesTags: [{ type: "PeerSupportWorker", id: "APPROVAL_COUNTS" }],
    }),
    adminGetWorkerCaseCounts: builder.query({
      query: () => "/api/peer-support/admin/worker-case-counts",
      providesTags: [{ type: "PeerSupportWorker", id: "CASE_COUNTS" }],
    }),
    handoffPeerSupportRequest: builder.mutation({
      query: ({ id, toWorkerId }) => ({
        url: `/api/peer-support/requests/${id}/handoff`,
        method: "PATCH",
        body: { toWorkerId },
      }),
      invalidatesTags: (r, e, { id }) => [
        { type: "PeerSupportRequest", id },
        { type: "PeerSupportRequest", id: "WORKER_ACTIVE" },
        { type: "PeerSupportWorker", id: "CASE_COUNTS" },
      ],
    }),
    cancelPeerSupportHandoff: builder.mutation({
      query: (id) => ({
        url: `/api/peer-support/requests/${id}/handoff/cancel`,
        method: "PATCH",
      }),
      invalidatesTags: (r, e, id) => [
        { type: "PeerSupportRequest", id },
        { type: "PeerSupportRequest", id: "WORKER_ACTIVE" },
      ],
    }),
    adminReassignPeerSupportRequest: builder.mutation({
      query: ({ id, toWorkerId }) => ({
        url: `/api/peer-support/admin/requests/${id}/reassign`,
        method: "PATCH",
        body: { toWorkerId },
      }),
      invalidatesTags: (r, e, { id }) => [
        { type: "PeerSupportRequest", id },
        { type: "PeerSupportRequest", id: "ADMIN_LIST" },
        { type: "PeerSupportWorker", id: "CASE_COUNTS" },
      ],
    }),
  }),
});

export const {
  useGetOnCallWorkersQuery,
  useGetPeerSupportCategoriesQuery,
  useSubmitPeerSupportRequestMutation,
  useGetMyPeerSupportRequestsQuery,
  useGetPeerSupportRequestQuery,
  useGetMyPeerSupportTeamQuery,
  useUpdateMyPeerSupportTeamPreferenceMutation,
  useGetWorkerPendingRequestsQuery,
  useGetWorkerActiveRequestsQuery,
  useGetWorkerCompletedRequestsQuery,
  useAcceptPeerSupportRequestMutation,
  useClosePeerSupportRequestMutation,
  useTogglePeerSupportOnCallMutation,
  useGetMyPeerSupportWorkerStatusQuery,
  useAdminGetAllPeerSupportRequestsQuery,
  useAdminGetPeerSupportStatsQuery,
  useGetPeerSupportSettingsQuery,
  useUpdatePeerSupportSettingsMutation,
  useGetPeerSupportWorkerRosterQuery,
  useAdminAddPeerSupportWorkerMutation,
  useAdminRemovePeerSupportWorkerMutation,
  useAdminGetWorkerApprovalCountsQuery,
  useAdminGetPeerSupportCategoriesQuery,
  useAdminCreatePeerSupportCategoryMutation,
  useAdminUpdatePeerSupportCategoryMutation,
  useAdminDeletePeerSupportCategoryMutation,
  useAdminGetPeerSupportDispositionsQuery,
  useAdminCreatePeerSupportDispositionMutation,
  useAdminUpdatePeerSupportDispositionMutation,
  useAdminDeletePeerSupportDispositionMutation,
  useAdminGetWorkerCaseCountsQuery,
  useHandoffPeerSupportRequestMutation,
  useCancelPeerSupportHandoffMutation,
  useAdminReassignPeerSupportRequestMutation,
} = peerSupportApi;
